import { CouponSwitchMenu } from "@components/CouponSwitchMenu";
import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import { appToast } from "@components/Toast";
import { AUTO_POPULATED_FLAG } from "@screen/Home/pages/GasShop/products";
import { buildXContextHeader } from "@utils/api/headers";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollView, Spinner, Text, YStack } from "tamagui";
import { submitOrder, updateDraftOrder } from "./api/kioskApi";
import AddressPickerSheet from "./components/AddressPickerSheet";
import CheckoutGuide, { CheckoutField } from "./components/CheckoutGuide";
import { Card, EditRow, formatDeliveryDate, SectionLabel } from "./components/ConfirmParts";
import KioskOrderSummary from "./components/KioskOrderSummary";
import { RefundPolicyNote } from "@components/payment/TapPayCompliance";
import { KioskCTA, KioskHeader } from "./components/KioskParts";
import OrderItemsCard from "./components/OrderItemsCard";
import PointsRedeemCard from "./components/PointsRedeemCard";
import OtpCard from "./components/OtpCard";
import { shouldBlockSubmit, shouldShowOtpCard } from "./otpPolicy";
import PhoneSheet from "./components/PhoneSheet";
import "./kiosk.scss";
import { kiosk } from "./kioskTheme";
import { useKiosk } from "./useKiosk";
import { useKioskCheckout } from "./useKioskCheckout";
import { useCartPointsRedeem } from "@screen/Home/pages/GasShop/utils/useCartPointsRedeem";
import { track, clearEntrySource } from "@lib/tracker";
import api from "@services/axios/apiManager";
import { isPracticeMode } from "@utils/practice/practiceMode";

const KioskConfirm = () => {
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity, userData } = useUser();
  const { productList, draftOrderId, deliveryTime, customerCouponId, cartProductTotalPrice, clearProducts, hideAmountOnPending, syncDraft } = useOrder();
  const { contactPhone, setContactPhone, invoiceType, invoiceCarrier, invoiceTax, otpVerified, setOtpVerified, needCheckoutOtp, setPhoneVerifiedTicket, setFirstCheckoutDone, hidePrice, resetCheckout, setLastSuccess, showInvoice, isMonthly, selectedPayment, allowElectronicPay } = useKiosk();
  // 電子付款開關（supplier platform_setting，PW 後台控制；關閉時付款方式維持唯讀貨到/月結，送出照舊）
  const elecOn = allowElectronicPay;
  // 已決議的付款方式文字（供成功頁）：選信用卡→卡標籤；選 Apple Pay→「Apple Pay」；否則月結/貨到（後端仍為權威）
  const paymentText =
    elecOn && selectedPayment?.type === "credit_card"
      ? selectedPayment.label
      : elecOn && selectedPayment?.type === "apple_pay"
        ? "Apple Pay"
        : isMonthly
          ? "月結"
          : "貨到付款";
  // 確認頁「付款方式」列：電子付款開啟且尚未選 → 顯示提示（不預設貨到付款）；其餘同 paymentText
  const paymentRowText = elecOn && !selectedPayment ? "請選擇" : paymentText;
  const xContext = buildXContextHeader(chooseIdentity) || "";

  // 算費 + 自動帶最優券 + 同步折扣到 store（與購物車共用同一套邏輯）
  const { preview, previewLoading } = useKioskCheckout();
  const [submitting, setSubmitting] = useState(false);
  const [editField, setEditField] = useState<CheckoutField | null>(null);
  const [showAddr, setShowAddr] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  // 首次結帳的簡訊 OTP 碼（內嵌於結算頁）
  const [otpCode, setOtpCode] = useState<string[]>(["", "", "", "", ""]);
  // null = 尚未載入；載入後若無可用券且未選券則隱藏整個優惠券欄位（與訂購頁一致）
  const [availableCouponCount, setAvailableCouponCount] = useState<number | null>(null);

  const address = chooseIdentity?.choose_address?.full_address || "尚未設定地址";
  // 送出成功會清空購物車，避免下方「空車退回首頁」effect 搶在導向成功頁之前觸發
  const submitted = useRef(false);

  // 沒有草稿/品項直接進來 → 退回首頁（送出成功後不適用，改導向成功頁）
  useEffect(() => {
    if (submitted.current) return;
    // 練習模式：草稿有偶發去同步會讓購物車瞬間變空，照樣退回首頁會形成迴圈。
    // 練習假後端抓草稿時會保底補品項，所以這裡不退回，改重抓一次草稿把品項帶回來。
    if (isPracticeMode()) {
      if (!draftOrderId || productList.length === 0) {
        api
          .get("/app-api/platform-order-drafts/current", { headers: { "X-Context": xContext } })
          .then((res) => {
            const d = res?.data?.data?.draft;
            if (d) syncDraft(d);
          })
          .catch(() => {});
      }
      return;
    }
    if (!draftOrderId || productList.length === 0) navigate("/kiosk", { replace: true });
  }, [draftOrderId, productList.length, navigate]);

  useEffect(() => {
    if (draftOrderId && productList.length > 0) {
      track("funnel_step", { step: "confirm_view" });
    }
  }, []);

  // 電話預帶
  useEffect(() => {
    if (!contactPhone && userData?.phone) setContactPhone(userData.phone);
  }, [userData?.phone, contactPhone, setContactPhone]);

  // 摘要文字（成功頁用，維持單行）
  const timeLabel =
    deliveryTime.type === "immediate"
      ? "立即配送（下單後將儘速為您安排配送人員）"
      : deliveryTime.date && deliveryTime.slotLabel
        ? `${formatDeliveryDate(deliveryTime.date)} ${deliveryTime.slotLabel}`
        : "尚未選擇";
  // 配送時段列：時段為主、日期為輔，方便一眼看出
  const timeRowValue =
    deliveryTime.type === "immediate" ? (
      <Text fontSize={15.5} fontWeight="800" color={kiosk.ink} mt={2}>
        立即配送{" "}
      </Text>
    ) : deliveryTime.date && deliveryTime.slotLabel ? (
      <Text fontSize={15.5} fontWeight="800" color={kiosk.ink} mt={2} letterSpacing={-0.2}>
        {formatDeliveryDate(deliveryTime.date)}{" "}
        <Text color={kiosk.grey} fontWeight="600">
          ·
        </Text>{" "}
        {deliveryTime.slotLabel}
      </Text>
    ) : (
      <Text fontSize={15} fontWeight="700" color={kiosk.danger} mt={1}>
        尚未選擇
      </Text>
    );
  const invoiceLabel =
    invoiceType === "carrier" ? `載具（${invoiceCarrier}）` : invoiceType === "tax_id" ? `統編（${invoiceTax}）` : invoiceType === "other" ? "其他" : "捐贈";
  const cnt = productList.reduce((s, i) => s + i.qty, 0);

  // 點數折抵（與瓦斯訂購頁共用同一支計算）。不顯示金額的單子（專員報價／瓦斯行隱藏金額）
  // 算不出折抵金額，改走「意願」模式：只收要不要折，由瓦斯行定價時套用。
  // 這種單子的 pointsResult 不可用來改總額 —— 否則 session 裡殘留的折抵選擇會偷偷把總額改掉。
  const pointsUsable = !hideAmountOnPending && !hidePrice;
  const { result: pointsResult } = useCartPointsRedeem();
  const pointsDiscount = pointsUsable ? pointsResult.discount : 0;
  const total = Math.max(0, (preview?.total ?? cartProductTotalPrice) - pointsDiscount);

  // 按下「確認送出」：先驗證欄位，第一次結帳需先過簡訊 OTP，再實際送出。
  const commitOrder = async () => {
    if (submitting || !draftOrderId) return;
    if (deliveryTime.type === "scheduled" && (!deliveryTime.date || !deliveryTime.slotId)) {
      appToast.error("請選擇配送時段");
      setEditField("time");
      return;
    }
    if (!validateTaiwanPhone(contactPhone)) {
      appToast.error("請填寫有效的聯絡電話");
      setShowPhone(true);
      return;
    }
    // 首次結帳需完成簡訊 OTP 驗證（本 session 已驗過則略過，與熟客/歷史訂單無關）。
    // otpVerified 由 OtpCard 向後端驗證成功後才會設為 true，這裡只做守門，不再前端接受任意碼。
    if (shouldBlockSubmit({ needCheckoutOtp, otpVerified })) {
      appToast.error(otpCode.join("").length !== 6 ? "請輸入簡訊驗證碼" : "驗證碼尚未通過驗證，請重新確認");
      return;
    }
    await doSubmit();
  };

  // 實際送出訂單（OTP 驗證通過後才會走到）
  const doSubmit = async () => {
    if (submitting || !draftOrderId) return;
    setSubmitting(true);
    try {
      const payload = {
        customer_address_id: chooseIdentity?.choose_address?.customer_address_id,
        contact_phone: contactPhone,
        // 電子付款關閉 → 一律貨到付款（維持原行為）；開啟且選信用卡 → 帶已綁卡 id；選 Apple Pay → 帶 "apple_pay"
        // （後端 platform-orders.service 依 supplier enable_electronic_payment 同閘門支援，僅標記不呼叫 TapPay）。
        // 後端訂單送出支援電子付款前，正式環境 flag 關閉，此處等同 "cash_on_delivery"。
        payment_method_ref:
          elecOn && selectedPayment?.type === "credit_card"
            ? selectedPayment.paymentMethodId
            : elecOn && selectedPayment?.type === "apple_pay"
              ? "apple_pay"
              : "cash_on_delivery",
        // 平台關閉發票區、或客戶選「其他」→ 發票欄位全帶 null（開一般 B2C 發票）。
        invoice_type: showInvoice && invoiceType !== "other" ? invoiceType : null,
        invoice_carrier: showInvoice && invoiceType === "carrier" ? invoiceCarrier : null,
        invoice_tax_id: showInvoice && invoiceType === "tax_id" ? invoiceTax : null,
        delivery_mode: deliveryTime.type === "scheduled" ? "reserve" : "immediate",
        delivery_date: deliveryTime.date,
        delivery_slot_id: deliveryTime.slotId,
        delivery_slot_source: deliveryTime.slotSource,
        customer_coupon_id: customerCouponId || null,
        // TODO(points-api): 點數折抵目前只在前端計算與顯示，尚未送給後端（端點與 DTO 都還沒有，
        // 多送未知欄位可能被 whitelist 擋成 400）。端點到位後補 point_redeem 與 point_redeem_intent
        // 兩個欄位（後者給隱藏金額／專員報價的單子），由後端重算折抵。與 GasShop/cart.tsx 同一處理。
      };
      const upd = await updateDraftOrder({ draftId: draftOrderId, body: payload, jwtToken: cusJwtToken || "", xContext });
      if (upd.state !== "success") {
        setSubmitting(false);
        return;
      }
      const submitClientRequestId = crypto.randomUUID();
      track("order_submit_click", { client_request_id: submitClientRequestId });
      const sub = await submitOrder({
        body: { draft_id: draftOrderId, client_request_id: submitClientRequestId, customer_coupon_id: customerCouponId || null },
        jwtToken: cusJwtToken || "",
        xContext,
      });
      if (sub.state === "success") {
        const orderId = sub.data?.data?.platform_order_id || "";
        // 訂單編號顯示用人類可讀的 order_list.order_id（snapshot 內），非 platform_order_id UUID。
        const displayId = sub.data?.data?.supplier_order_snapshot_json?.order_list?.order_id || "";
        submitted.current = true; // 鎖住空車退回首頁的 effect
        setFirstCheckoutDone(true);
        sessionStorage.removeItem(AUTO_POPULATED_FLAG);
        // 成功資訊存入 store → 導向獨立成功頁（重新整理不消失）
        setLastSuccess({
          orderNo: displayId ? `#${displayId}` : "訂單已建立",
          orderId,
          itemCount: cnt,
          total,
          timeText: timeLabel,
          paymentText,
          address,
          clientRequestId: submitClientRequestId,
        });
        clearProducts();
        resetCheckout();
        navigate("/kiosk/success", { replace: true });
      } else {
        setSubmitting(false);
      }
    } catch (e) {
      console.error("commitOrder error", e);
      setSubmitting(false);
    }
  };

  return (
        // 底色沿用全站的淡霧（.gas-skin-v2-bg，定義在 all.scss）而不是複製一份：
    // 兩份定義遲早會走鐘，而客戶在同一個 App 裡會前後看到這兩條流程。
    <YStack flex={1} position="relative" className="gas-skin-v2-bg">
      {/* 返回 → 回購物車並直接開在「怎麼付款？」步驟（電子付款關閉時照舊回購物車） */}
      <KioskHeader title="確認訂單" onBack={() => navigate("/kiosk/cart", elecOn ? { state: { reopenCheckout: "payment" } } : undefined)} />

      <ScrollView flex={1} className="kiosk-noscroll kiosk-stagger" contentContainerStyle={{ paddingTop: 0, paddingLeft: 16, paddingRight: 16, paddingBottom: 24 }}>
        {/* 配送資訊 */}
        <SectionLabel>配送資訊</SectionLabel>
        <Card>
          <EditRow icon="pin" k="送達地址" v={address} onPress={() => setShowAddr(true)} />
          <EditRow icon="clock" k="配送時段" v={timeRowValue} onPress={() => setEditField("time")} top />
          <EditRow icon="phone" k="聯絡電話" v={contactPhone || "尚未填寫"} onPress={() => setShowPhone(true)} top />
        </Card>

        {/* 內嵌簡訊 OTP 驗證區塊。身分/鋼瓶/配件/合約已於 FirstJoinSetup 完成。
            驗證成功後不整張消失，改由 OtpCard 顯示「✓ 驗證成功」；alreadyVerified 讓重新進頁不重送。
            只有「註冊過程沒驗過手機」的人才在結帳補驗（實務上＝QR 掃 barcode 免 OTP 綁定）。
            其餘一律不出現：QR 走過 OTP、傳單註冊時已驗、rich menu 進來的既有客戶視為驗過。 */}
        {shouldShowOtpCard({ needCheckoutOtp, otpVerified }) && (
          <OtpCard code={otpCode} setCode={setOtpCode} phone={contactPhone} alreadyVerified={otpVerified} />
        )}

        {/* 商品：排在付款之前 —— 「買什麼」是這頁的主體，要先確認得了內容，
            才輪得到「怎麼付」。折抵折在瓦斯價，與下方訂單摘要的應付總額一致。 */}
        <OrderItemsCard productList={productList} count={cnt} couponDiscount={preview?.coupon_discount ?? 0} hidePrice={hideAmountOnPending || hidePrice} />

        {/* 付款與發票（發票區受平台設定 show_invoice 控制） */}
        <SectionLabel>{showInvoice ? "付款與發票" : "付款"}</SectionLabel>
        <Card>
          <EditRow icon="cash" k="付款方式" v={paymentRowText} onPress={elecOn ? () => setEditField("payment") : undefined} />
          {showInvoice && (
            <EditRow
              icon={invoiceType === "donate" ? "heart" : invoiceType === "tax_id" ? "building" : invoiceType === "other" ? "receipt" : "mobile"}
              k="發票"
              v={invoiceLabel}
              onPress={() => setEditField("invoice")}
              top
            />
          )}
        </Card>

        {/* 優惠券：自動帶入最優、可手動換券（沒有可用券且未選券時整欄隱藏）。
            換券會 setCustomerCouponId → 上方算費 effect 重新 preview → 折抵即時反映在瓦斯價與應付總額 */}
        {(availableCouponCount === null || availableCouponCount > 0 || !!customerCouponId) && (
          <>
            <SectionLabel>優惠券</SectionLabel>
            <Card>
              {/* amountAccent：折抵金額做成綠色膠囊而不是一行小字。
                  這是整個結帳流程裡唯一「客戶省到錢」的訊息，和旁邊的灰字排在一起會被滑過去。 */}
              <CouponSwitchMenu amountAccent themeColor={kiosk.ink} selectId={customerCouponId || ""} onLoaded={setAvailableCouponCount} />
            </Card>
          </>
        )}

        {/* 點數折抵：券之後才折（存氣點數 → 帳戶點數），所以排在優惠券下面。
            隱藏金額／專員報價的單子走 intentOnly（只收意願，不出金額）。 */}
        <PointsRedeemCard intentOnly={!pointsUsable} />

        {/* 訂單摘要（對齊訂購頁結算頁：逐項折後價 + 小計/樓層費 + 應付）；
            瓦斯行隱藏金額（hideAmountOnPending）或專員報價/新客戶未定價（hidePrice）時整張不顯示 */}
        {!hideAmountOnPending && !hidePrice && (
          <KioskOrderSummary
            preview={preview}
            productList={productList}
            count={cnt}
            total={total}
            loading={previewLoading}
            points={pointsUsable ? pointsResult : null}
          />
        )}

        {/* 下單前揭露退換貨與取消政策（與帳號頁同一份文案） */}
        <RefundPolicyNote />
      </ScrollView>

      <YStack backgroundColor="#fff" borderTopWidth={1} borderColor={kiosk.line} px={16} pt={12} pb={22} style={{ flex: "0 0 auto" }}>
        <KioskCTA
          label={submitting ? "送出中…" : hideAmountOnPending || hidePrice ? "確認送出" : `確認送出 NT$ ${total.toLocaleString()}`}
          disabled={submitting || shouldBlockSubmit({ needCheckoutOtp, otpVerified })}
          onPress={commitOrder}
          right={submitting ? <Spinner size="small" color="#fff" /> : undefined}
        />
      </YStack>

      {/* 變更：時段 / 發票 */}
      {editField && (
        <CheckoutGuide fromConfirm startField={editField} onClose={() => setEditField(null)} onDone={() => setEditField(null)} />
      )}
      <AddressPickerSheet isOpen={showAddr} onClose={() => setShowAddr(false)} />
      <PhoneSheet
        isOpen={showPhone}
        initial={contactPhone}
        onClose={() => setShowPhone(false)}
        onSave={(v) => {
          // 換了門號 → 先前的 OTP 驗證對「舊號碼」才成立，必須重驗。
          // 不重置的話：註冊時驗過 A（otpVerified=true）→ 結算頁改成 B → OtpCard 因 verified
          // 已為 true 而不重送簡訊，送出按鈕也只看 otpVerified → B 完全沒驗證就送單。
          if (v !== contactPhone) {
            setOtpVerified(false);
            setPhoneVerifiedTicket("");
          }
          setContactPhone(v);
          setShowPhone(false);
        }}
      />
    </YStack>
  );
};

export default KioskConfirm;
